import { useEffect, useMemo } from 'react'
import { MdClose } from 'react-icons/md'

import { useAuth } from 'src/providers/AuthProvider'
import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { useMenuState } from 'src/providers/MenuStateProvider'
import { useUser } from 'src/providers/UserProvider'

import { LogoRebrand } from 'src/components/atoms/LogoRebrand'
import { DesktopNavigation } from 'src/components/layouts/Header/DesktopNavigation'
import { MobileNavigation } from 'src/components/layouts/Header/MobileNavigation'
import { MobileNavigationToggle } from 'src/components/layouts/Header/MobileNavigationToggle'

import { dashboardPath } from 'src/config/paths'
import { env } from 'src/env/client.mjs'
import useMediaQuery from 'src/hooks/useMediaQuery'
import { ActiveProfileType, NavigationContext } from 'src/types/account'
import { Stages } from 'src/types/stages'

import {
  buildAdminLinks,
  buildFunderLinks,
  buildPlanterLinks,
  buildPublicLinks,
  buildAdvisorLinks
} from './helper'

const FocussedActionHeader = () => {
  const { closeFocussedHeader } = useFocusActionState()
  const handleFocusClose = () => {
    closeFocussedHeader()
  }
  return (
    <nav
      id='main_nav'
      className='px-5 md:px-7.5 relative flex flex-row items-center justify-between w-full max-w-[1220px] place-self-center'
    >
      <div
        role='button'
        tabIndex={0}
        onClick={handleFocusClose}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleFocusClose()
          }
        }}
        className='relative w-10 h-10 lg:h-15 lg:w-15 cursor-pointer'
      >
        <LogoRebrand />
      </div>
      <MdClose
        tabIndex={0}
        className='cursor-pointer w-7 h-7 sm:w-8 sm:h-8'
        onClick={handleFocusClose}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            handleFocusClose()
          }
        }}
      />
    </nav>
  )
}

const DefaultNavContext = (activeProfileType?: string | null) => {
  if (activeProfileType === ActiveProfileType.Planter) {
    return NavigationContext.Planter
  }
  if (activeProfileType === ActiveProfileType.Funder) {
    return NavigationContext.Funder
  }
  return NavigationContext.Public
}

export const Header = ({
  navigationContext
}: {
  navigationContext?: NavigationContext
}) => {
  const { isStaff, isAdvisor } = useAuth()
  const { user, isAdminMode, isLoading } = useUser()
  const isMobile = useMediaQuery('(max-width: 640px)')
  const { isOpen, setIsOpen } = useMenuState()
  const isProduction = env.NEXT_PUBLIC_APP_STAGE === Stages.PRODUCTION
  const { isFocus } = useFocusActionState()

  const navContextWithDefault =
    navigationContext || DefaultNavContext(user?.ActiveProfileType)

  useEffect(() => {
    setIsOpen(false)
  }, [isMobile, setIsOpen])

  const links = useMemo(() => {
    if (isLoading) return []
    if (isStaff && isAdminMode) {
      return buildAdminLinks()
    }
    if (isAdvisor && isAdminMode) {
      return buildAdvisorLinks()
    }

    if (navContextWithDefault === 'funder') {
      if (user?.Account) {
        return buildFunderLinks()
      }
      return [
        {
          requiresUser: true,
          path: dashboardPath(),
          label: 'Account'
        }
      ]
    }

    if (navContextWithDefault === 'planter') {
      return buildPlanterLinks()
    }

    return buildPublicLinks()
  }, [isLoading, isStaff, isAdminMode, isAdvisor, navContextWithDefault, user])

  return (
    <header
      className={`header z-20 relative py-5 ${
        navigationContext === NavigationContext.Public || !user
          ? ''
          : 'bg-forestDeep text-white'
      } shadow-[0_15px_15px_0_rgba(0,0,0,.03)] ${
        isOpen && isMobile ? 'bg-linen! [&_.link]:text-forestDeep!' : ''
      }`}
      role='banner'
    >
      {isFocus ? (
        <FocussedActionHeader />
      ) : (
        <>
          <nav
            id='main_nav'
            className='relative flex flex-row justify-between px-0 sm:flex-nowrap sm:justify-start md:justify-center'
          >
            <DesktopNavigation
              links={links}
              navigationContext={navContextWithDefault}
            />
            <MobileNavigationToggle navigationContext={navContextWithDefault} />

            {isProduction ? null : (
              <div className='absolute p-2 text-center w-40 -left-10 bg-[rgba(255,255,0,.8)] bg-clip-border -rotate-45 shadow-[0_2px_2px_0_rgba(200,200,0,.5)] capitalize'>
                {env.NEXT_PUBLIC_APP_STAGE}
              </div>
            )}
          </nav>
          {isOpen ? (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              onClick={() => {
                setIsOpen(false)
              }}
              className='menu-overlay'
            >
              <MobileNavigation
                links={links}
                navigationContext={navContextWithDefault}
              />
            </div>
          ) : null}
        </>
      )}
    </header>
  )
}
